/** Layout primitives: page shell, section headings, stat tiles, breadcrumbs. */

import type { ReactNode } from "react";

export function Section({
  title,
  subtitle,
  children,
  actions,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section style={{ marginBottom: 34 }} className="fade-in">
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 className="serif" style={{ margin: 0, fontSize: 20, fontWeight: 400 }}>
            {title}
          </h2>
          {subtitle && (
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--slate)", maxWidth: 720 }}>
              {subtitle}
            </p>
          )}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function StatTile({
  label,
  value,
  note,
  tone = "neutral",
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "neutral" | "gold" | "rust" | "green";
}) {
  const colour =
    tone === "gold"
      ? "var(--gold)"
      : tone === "rust"
        ? "var(--rust)"
        : tone === "green"
          ? "var(--green)"
          : "var(--parchment)";
  return (
    <div
      className="card"
      style={{ padding: "16px 18px", minWidth: 0 }}
      role="group"
      aria-label={label}
    >
      <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div className="serif num" style={{ fontSize: 26, color: colour, marginTop: 6 }}>
        {value}
      </div>
      {note && <div style={{ fontSize: 11.5, color: "var(--slate)", marginTop: 4 }}>{note}</div>}
    </div>
  );
}

export function Breadcrumb({
  items,
}: {
  items: { label: string; onClick?: () => void }[];
}) {
  return (
    <nav aria-label="Breadcrumb" style={{ fontSize: 12.5, marginBottom: 18, color: "var(--slate)" }}>
      {items.map((item, index) => (
        <span key={item.label}>
          {index > 0 && <span style={{ margin: "0 8px", opacity: 0.5 }}>/</span>}
          {item.onClick ? (
            <button
              onClick={item.onClick}
              style={{
                background: "none",
                border: "none",
                color: "var(--gold)",
                padding: 0,
                fontSize: 12.5,
              }}
            >
              {item.label}
            </button>
          ) : (
            <span style={{ color: "var(--parchment)" }}>{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function Notice({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "warn" }) {
  return (
    <div
      style={{
        border: `1px ${tone === "warn" ? "solid" : "dashed"} ${
          tone === "warn" ? "var(--rust)" : "var(--line-strong)"
        }`,
        borderRadius: 8,
        padding: "12px 14px",
        fontSize: 12.5,
        color: "var(--slate)",
        lineHeight: 1.6,
        background: tone === "warn" ? "rgba(180,85,63,0.08)" : "transparent",
      }}
    >
      {children}
    </div>
  );
}
